import React, { useContext, useEffect } from 'react'
import withDialog, { WrappedProps } from 'shared/hocs/withDialog'
import styled, { ThemeContext } from 'styled-components'
import { lighten } from 'polished'

import cloneDeep from 'lodash/cloneDeep'
import findIndex from 'lodash/findIndex'
import flowRight from 'lodash/flowRight'
import upperCase from 'lodash/upperCase'
import pick from 'lodash/pick'
import Button from 'shared/components/Button'
import Box from 'shared/components/Box'
import Loader from 'shared/components/Loader'
import DefaultImage from 'assets/images/launchpad_logo.png'
import ScreeningForm from 'shared/components/Screening/components/ScreeningForm'
import useQuery from 'shared/hooks/useQuery'
import { organizationSettingSelector } from 'shared/hocs/withAuth'
import { useSelector } from 'react-redux'
import {
  ScreeningFormType,
  ScreeningAnswerType,
  ScreeningQuestionType,
  ScreeningParticipantType,
  getDummyQuestions,
} from 'shared/components/Screening/models/screening'

import { QuestionFields } from 'shared/components/Screening/models/question'

type Props = WrappedProps<ScreeningFormType> & {
  isPreview?: boolean
}

// const questions = getDummyQuestions()
function FormDialog(props: Props) {
  const { formState, formHandlers, isProcessing, isPreview = false } = props

  const theme = useContext(ThemeContext)
  const { onChange, onSetForm } = formHandlers
  const { fields } = formState
  const { organization_information } = useSelector(organizationSettingSelector)
  const [{ loading, data: quetionFields }] = useQuery<QuestionFields[]>(
    { url: 'note/screening/questions' },
    { initialData: [], isBase: false },
  )

  const formatted = quetionFields
    ?.filter(question => ['RADIO', 'RICHTEXT'].includes(upperCase(question?.type)))
    ?.map(qf => pick(qf, ['options', 'label', 'id', 'type', 'section_text']))

  const questions = formatted?.filter(question => upperCase(question?.type) === 'RADIO')

  useEffect(() => {
    const participants = fields?.participants?.map((participant: ScreeningParticipantType) => {
      return { ...participant, question_count: questions?.length }
    })

    onSetForm({ ...fields, participants })
  }, [questions?.length])

  return (
    <FormDialogBodyContainer>
      <div className="form-logo-container">
        <img src={DefaultImage} />
      </div>
      <div className="form-description">
        This form is for each individual attending {organization_information.name}. <br />
        All fields are mandatory
      </div>
      {loading && (
        <Box my="50px">
          <Loader />
        </Box>
      )}
      {!loading && (
        <div className="form-content">
          {fields?.participants?.map((participant: ScreeningParticipantType, index: number) => (
            <ScreeningForm
              questions={formatted}
              isPreview={isPreview}
              isProcessing={isProcessing}
              participant_id={participant.id}
              answers={participant.answers}
              onChange={handleChange(participant.id)}
              key={`screening-form-${index}`}
            />
          ))}
        </div>
      )}
    </FormDialogBodyContainer>
  )

  function handleChange(participant_id: string) {
    return (answers: ScreeningAnswerType[]) => {
      const participants = cloneDeep(fields?.participants || [])
      const index = findIndex(participants, { id: participant_id })

      participants.splice(index, 1, { ...participants[index], answers })
      onChange('participants', participants)
    }
  }
}

const WrappedFormDialog = flowRight(withDialog())(FormDialog)

const FormDialogBodyContainer = styled(Box)`
  color: ${({ theme }) => theme.colors.secondary};
  padding: 1em 1em 0;
  width: 100%;
  max-width: 700px;

  .form-logo-container {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;

    img {
      -webkit-user-drag: none;
      -khtml-user-drag: none;
      -moz-user-drag: none;
      -o-user-drag: none;
      user-drag: none;

      width: 100%;
      max-width: 110px;
      max-height: 88px;
    }
  }

  .form-description {
    width: 100%;
    max-width: 600px;
    margin-bottom: 40px;

    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    color: ${({ theme }) => theme?.colors?.secondary};
    text-align: center;
  }

  .form-content {
    max-height: 500px;
    width: 100%;
    overflow: hidden;
    overflow-y: auto;
    margin-right: -32px;

    & > * {
      margin-bottom: 40px;
      width: 100%;
    }

    ::-webkit-scrollbar-thumb {
      background-color: #919191;
      border-radius: 50px;
    }

    ::-webkit-scrollbar {
      width: 12px;
      height: 8px;
      background-color: #f5f5f5;
      border-radius: 5px;
    }
  }
`

WrappedFormDialog.defaultProps = {
  continueIsDisabled(data: ScreeningFormType) {
    return data?.participants?.some((participant: ScreeningParticipantType) => {
      return participant?.question_count !== participant?.answers?.length || (participant?.question_count || 0) === 0
    })
  },
}

export default WrappedFormDialog
